<script setup>
import { computed } from 'vue';
import XlAvatar from '../xl/XlAvatar.vue';
import XlTag from '../xl/XlTag.vue';

/**
 * Who is running a session (XN-4745): the host first, tagged, then every
 * speaker, never folded. The API never repeats the host as a speaker, so two
 * people who share a name both show; rows are keyed by position, as the API
 * sends no ids. A guest invited by email has no name and is not listed.
 * Shared by the session's panel (level 3) and its page (level 2).
 */
const props = defineProps({
  host: { type: Object, default: null },
  speakers: { type: Array, default: () => [] },
  level: { type: Number, default: 3 },
});

const people = computed(() => [
  ...(props.host?.name ? [{ ...props.host, host: true }] : []),
  ...props.speakers.filter((person) => person?.name),
]);
</script>

<template>
  <section v-if="people.length" class="speakers" data-testid="session-speakers">
    <component :is="`h${level}`" class="speakers__h">Speakers</component>
    <div v-for="(person, index) in people" :key="index" class="speakers__row" data-testid="speaker">
      <XlAvatar :name="person.name" :src="person.photo || ''" :size="32" />
      <div class="speakers__who">
        <div class="speakers__line">
          <b class="speakers__name">{{ person.name }}<template v-if="person.you"> — you</template></b>
          <XlTag v-if="person.host" tone="brand">Host</XlTag>
        </div>
        <span v-if="person.description" class="speakers__desc" data-testid="speaker-description">{{ person.description }}</span>
      </div>
    </div>
  </section>
</template>

<style scoped>
/* Prototype cpDetails (build 15 Oct): a sentence-case label, then the rows on the
   panel's own background, no card and no rules. Rows match PortalSessionFiles' and
   start at the top, so beside Files each name sits on the file name's line, with
   or without a line under it. */
.speakers__h { margin: 0 0 9px; font-size: 13px; font-weight: 700; color: var(--xl-color-on-surface); }
.speakers__row { display: flex; align-items: flex-start; gap: 10px; min-height: 52px; padding: 6px 0; box-sizing: border-box; }
.speakers__who { min-width: 0; display: flex; flex-direction: column; }
.speakers__line { display: flex; align-items: center; gap: 7px; flex-wrap: wrap; }
/* The tag is taller than the name; it doesn't push the name down. */
.speakers__line :deep(.xl-tag) { margin-block: -2px; }
.speakers__name { font-size: 14px; overflow-wrap: anywhere; }
.speakers__desc { font-size: 12.5px; color: var(--xl-color-secondary-text); }
</style>
